import { useEffect, useState } from 'react';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';

import LeftMenu from '../../components/leftMenu';
import Pagination from '../../components/pagination';
import useAxiosAdminFetch from '../../hooks/useAxiosAdminFetch';
import useStoreNo from '../../hooks/useStoreNo';
import NoticeDetail from '../admin/NoticeDetail';
import './noticeManage.css';

/**
 * [2026-10-28] 상점관리자 공지사항 — PC 화면(/:storeNo/notice, ?id=N 이면 상세).
 *  왼쪽 메뉴 "공지사항"이 앱용 화면(/mobile/notice, 앱 헤더 · 더 보기 버튼)으로 가던 것을
 *  상점 정보 관리 · 공지 관리와 같은 틀(왼쪽 메뉴 + 카드 + 표)로 따로 만들었다.
 *  데이터는 앱 화면과 같은 API(GET notice/list · notice/:id) — 운영자 + 참여 채널의 상점관리자 대상 공지.
 *  앱(웹뷰)은 계속 /mobile/notice 를 쓴다(router.js · app_shell 예외 그대로).
 */

const PAGE_SIZE = 10;
const NEW_BADGE_MS = 7 * 24 * 60 * 60 * 1000;
const AUTHOR_LABEL = { operator: '내기프트', channel: '채널' };
const KIND_LABEL = { COUPON_EVENT: '쿠폰이벤트', ROULETTE_EVENT: '룰렛이벤트' };

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())}`;
}

function isNew(value) {
  if (!value) return false;
  const t = new Date(value).getTime();
  if (Number.isNaN(t)) return false;
  const diff = Date.now() - t;
  return diff >= 0 && diff <= NEW_BADGE_MS;
}

export default function NoticeBoardPc() {
  const storeNo = useStoreNo();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const { fetchData } = useAxiosAdminFetch();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPage, setTotalPage] = useState(1);
  const [pageNo, setPageNo] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const listPath = storeNo ? `/${storeNo}/notice` : '/mobile/notice';

  useEffect(() => {
    if (id !== null) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await fetchData({
          method: 'GET',
          url: `${process.env.REACT_APP_API_SERVER}notice/list?pageNo=${pageNo}&pageSize=${PAGE_SIZE}`,
        });
        if (cancelled) return;
        const payload = res?.data?.data;
        if (!payload) {
          setRows([]);
          setError(true);
          return;
        }
        setRows(Array.isArray(payload.list) ? payload.list : []);
        setTotal(payload.total ?? payload.totalCount ?? 0);
        setTotalPage(payload.totalPage || 1);
      } catch (e) {
        console.error('notice list load error:', e);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchData, pageNo, id]);

  return (
    <>
      <section className="location_box location_box_mypage">
        <ul className="location_001">
          <li>
            <NavLink to={storeNo ? `/${storeNo}/gift-list` : '/gift-list'}>홈</NavLink>
          </li>
          <li>공지사항</li>
        </ul>
      </section>

      <main>
        <section className="contants_box contants_box_mypage">
          <LeftMenu pageName="notice" />

          <section className="rightcontant_box notice-manage">
            <h2 className="tit_01" style={{ margin: 0 }}>공지사항</h2>
            <p className="notice-manage__desc">내기프트와 참여 중인 채널이 상점관리자에게 보내는 공지입니다.</p>

            {id !== null ? (
              <article className="form_card notice-board__detail">
                <NoticeDetail id={id} formatDate={formatDate} onList={() => navigate(listPath)} />
                <div className="notice-board__detail-foot">
                  <button type="button" className="button_04 button_gray_line" onClick={() => navigate(listPath)}>
                    목록으로
                  </button>
                </div>
              </article>
            ) : (
              <article className="form_card list_card">
                <table className="table_box_01">
                  <colgroup>
                    <col width="70px" />
                    <col width="120px" />
                    <col />
                    <col width="140px" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>보낸 곳</th>
                      <th style={{ textAlign: 'left' }}>제목</th>
                      <th>등록일</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={4} style={{ textAlign: 'center' }}>불러오는 중...</td>
                      </tr>
                    ) : error ? (
                      <tr>
                        <td colSpan={4} style={{ textAlign: 'center' }}>
                          공지를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.
                        </td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td colSpan={4} style={{ textAlign: 'center' }}>등록된 공지가 없습니다.</td>
                      </tr>
                    ) : (
                      rows.map((row, idx) => (
                        <tr
                          key={row.noticeNo}
                          className="notice-board__row"
                          onClick={() => navigate(`${listPath}?id=${row.noticeNo}`)}
                        >
                          <td>{total ? total - ((pageNo - 1) * PAGE_SIZE + idx) : (pageNo - 1) * PAGE_SIZE + idx + 1}</td>
                          <td>
                            <span className={`notice-board__author notice-board__author--${row.authorType || 'operator'}`}>
                              {AUTHOR_LABEL[row.authorType] || '내기프트'}
                            </span>
                          </td>
                          <td style={{ textAlign: 'left' }}>
                            {KIND_LABEL[row.noticeKind] && (
                              <span className={`notice-manage__kind notice-manage__kind--${row.noticeKind}`} style={{ marginRight: 6 }}>
                                {KIND_LABEL[row.noticeKind]}
                              </span>
                            )}
                            <span className="notice-board__title">{row.title}</span>
                            {isNew(row.startDttm) && <span className="notice-board__new">NEW</span>}
                          </td>
                          <td className="gift-table__date">{formatDate(row.startDttm)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                <ul className="page_box">
                  <Pagination totalPage={totalPage} onPageChange={setPageNo} />
                </ul>
              </article>
            )}
          </section>
        </section>
      </main>
    </>
  );
}
